import { Fragment, type ReactNode } from 'react';

/**
 * Renders WhatsApp's own formatting markup the way the WhatsApp app does, so a
 * message shown in Chat Logs looks like what the customer saw on their phone:
 *
 *   *bold*  _italic_  ~strike~  `code`  ```monospace```  > quote  * / - bullet
 *
 * WhatsApp's rules, not Markdown's: a marker only counts when it hugs the text
 * (`*bold*`, never `* bold *`) and isn't glued to a word on the outside
 * (`2*3*4` stays literal). Output is React nodes, never HTML strings.
 */

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Group order: 1 inline code, 2 bold, 3 italic, 4 strikethrough.
const INLINE_RE =
  /`([^`\n]+)`|(?<![\p{L}\p{N}*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\p{L}\p{N}*])|(?<![\p{L}\p{N}_])_(?!\s)([^_\n]+?)(?<!\s)_(?![\p{L}\p{N}_])|(?<![\p{L}\p{N}~])~(?!\s)([^~\n]+?)(?<!\s)~(?![\p{L}\p{N}~])/gu;

const monoSx = {
  fontFamily: MONO_FONT,
  fontSize: '0.92em',
} as const;

function renderInline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const k = `${key}.${i++}`;
    const [, code, bold, italic, strike] = m;
    if (code !== undefined) {
      out.push(
        <code key={k} style={{ ...monoSx, background: 'rgba(0,0,0,0.06)', borderRadius: 4, padding: '0 4px' }}>
          {code}
        </code>
      );
    } else if (bold !== undefined) {
      out.push(<strong key={k}>{renderInline(bold, k)}</strong>);
    } else if (italic !== undefined) {
      out.push(<em key={k}>{renderInline(italic, k)}</em>);
    } else if (strike !== undefined) {
      out.push(<s key={k}>{renderInline(strike, k)}</s>);
    }
    last = start + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** Line-level rules (quote, bullet) + inline markup for text outside ``` blocks. */
function renderLines(text: string, key: string): ReactNode[] {
  const lines = text.split('\n');
  const out: ReactNode[] = [];
  lines.forEach((line, idx) => {
    const k = `${key}.l${idx}`;
    const isLast = idx === lines.length - 1;
    const quote = /^>\s(.*)$/.exec(line);
    if (quote) {
      // display:block already ends the line, so its "\n" is dropped.
      out.push(
        <span
          key={k}
          style={{ display: 'block', borderLeft: '3px solid rgba(0,0,0,0.2)', paddingLeft: 8, opacity: 0.85 }}
        >
          {renderInline(quote[1] ?? '', k)}
        </span>
      );
      return;
    }
    const bullet = /^(\s*)[*-]\s(.*)$/.exec(line);
    out.push(
      <Fragment key={k}>
        {bullet ? [bullet[1], '• ', ...renderInline(bullet[2] ?? '', k)] : renderInline(line, k)}
        {!isLast && '\n'}
      </Fragment>
    );
  });
  return out;
}

export function renderWhatsAppText(text: string): ReactNode {
  if (!text) return text;
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  // ``` blocks may span lines and take no further formatting, so cut them out first.
  for (const m of text.matchAll(/```([\s\S]+?)```/g)) {
    const start = m.index ?? 0;
    if (start > last) out.push(...renderLines(text.slice(last, start), `s${i}`));
    out.push(
      <span key={`m${i}`} style={monoSx}>
        {m[1]}
      </span>
    );
    last = start + m[0].length;
    i++;
  }
  if (last < text.length) out.push(...renderLines(text.slice(last), `s${i}`));
  return out;
}
